<template>
  <div>
    <b-row class="align-items-stretch" v-if="hasAnyAllocation">
      <h5 class="mb-7">Portfolio</h5>

      <b-col
        :cols="activeTab === 'others' ? 12 : undefined"
        :lg="activeTab === 'others' ? undefined : 7"
        class="mb-4"
      >
        <div v-if="hasAnyAllocation" class="card p-3 shadow-sm h-100 w-100">
          <h5 class="text-muted mb-4">Asset allocation</h5>

          <!-- Move the tab-row outside the column grid for full width -->
          <div class="tab-row d-flex justify-content-between w-100 mb-4" style="width: 100%;">
            <button
              class="custom-tab text-center flex-grow-1"
              :class="{ active: activeTab === 'equity', 'text-muted': !hasEquityAllocation }"
              :disabled="!hasEquityAllocation"
              @click="hasEquityAllocation && (activeTab = 'equity')"
            >
              Equity {{ formatPercent(schemeAssetAllocation.EquityAllocationPercentage) }}
            </button>
            <button
              class="custom-tab text-center flex-grow-1"
              :class="{ active: activeTab === 'debt', 'text-muted': !hasDebtAllocation }"
              :disabled="!hasDebtAllocation"
              @click="hasDebtAllocation && (activeTab = 'debt')"
            >
              Debt {{ formatPercent(schemeAssetAllocation.DebtAllocationPercentage) }}
            </button>
            <button
              class="custom-tab text-center flex-grow-1"
              :class="{ active: activeTab === 'others', 'text-muted': !hasOtherAllocation }"
              :disabled="!hasOtherAllocation"
              @click="hasOtherAllocation && (activeTab = 'others')"
            >
              Others {{ formatPercent(schemeAssetAllocation.OtherAllocationPercentage) }}
            </button>
          </div>

          <!-- Equity Tab Content -->
          <div v-if="activeTab === 'equity' && hasEquityAllocation">
            <h6 class="mt-1 text-center" v-if="!isEquityMarketCapEmpty">
              <span
                style="background-color: #007bff"
                class="text-white px-3 py-2 rounded-pill d-inline-block"
              >
                Marketcap allocation
              </span>
            </h6>
            <template v-if="!isEquityMarketCapEmpty">
              <div class="mb-3 mt-2 align-items-center">
                <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                  <span>Largecap</span>
                  <span>{{ formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.LargeCapPercentage ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgba(0, 227, 150, 0.5)"
                      :style="{ width: formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.LargeCapPercentage ?? 0) }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="mb-3 mt-2 align-items-center">
                <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                  <span>Midcap</span>
                  <span>{{ formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.MidCapPercentage ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgb(0, 143, 251)"
                      :style="{ width: formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.MidCapPercentage ?? 0) }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="mb-3 mt-2 align-items-center">
                <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                  <span>Smallcap</span>
                  <span>{{ formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.SmallCapPercentage ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgb(255, 69, 96)"
                      :style="{ width: formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.SmallCapPercentage ?? 0) }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="mb-3 mt-2 align-items-center">
                <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                  <span>International Equity</span>
                  <span>{{ formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.OverseasPercentage ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: #205AA6"
                      :style="{ width: formatPercent(schemeAssetAllocation.EquityAllocation?.EquityMarketCap?.OverseasPercentage ?? 0) }"
                    ></div>
                  </div>
                </div>
              </div>
            </template>
        
            <template v-else>
              <div>
                <div class="mb-3 mt-2 align-items-center">
                  <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                    <span>Gross Equity %</span>
                    <span>{{ formatPercent(derivativesExposure?.GrossEquityPercentage ?? 0) }}</span>
                  </div>
                  <div class="d-flex align-items-center w-100 mt-3">
                    <div class="progress-bar-container me-2">
                      <div
                        class="progress-bar-fill"
                        style="background-color: #00E396"
                        :style="{ width: formatPercent(derivativesExposure?.GrossEquityPercentage ?? 0) }"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 mt-2 align-items-center">
                  <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                    <span>Derivatives Exposure</span>
                    <span>{{ formatPercent(derivativesExposure?.DerivativesExposure ?? 0) }}</span>
                  </div>
                  <div class="d-flex align-items-center w-100 mt-3">
                    <div class="progress-bar-container me-2">
                      <div
                        class="progress-bar-fill"
                        style="background-color: #008FFB"
                        :style="{ width: formatPercent(derivativesExposure?.DerivativesExposure ?? 0) }"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 mt-2 align-items-center">
                  <div class="me-3 d-flex justify-content-between" style="min-width: 120px">
                    <span>Net Equity</span>
                    <span>{{ formatPercent(derivativesExposure?.NetEquity ?? 0) }}</span>
                  </div>
                  <div class="d-flex align-items-center w-100 mt-3">
                    <div class="progress-bar-container me-2">
                      <div
                        class="progress-bar-fill"
                        :style="{
                          backgroundColor: (Number(derivativesExposure?.NetEquity) >= 0) ? '#FF4560' : undefined,
                          width: formatPercent(derivativesExposure?.NetEquity ?? 0)
                        }"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="text-muted mt-2" style="font-size: 12px;">
                  Last Modified: {{ derivativesExposureLastModifiedDate }}
                </div>
              </div>
            </template>
          </div>

          <!-- Debt Tab Content -->
          <div v-if="activeTab === 'debt' && hasDebtAllocation">
            <div class="tab-row-center">
              <div class="tab-row rounded-pill">
                <button
                  class="custom-tab text-center"
                  :class="{ active: activeDebtTab === 'credit' }"
                  @click="activeDebtTab = 'credit'"
                >
                  Credit quality
                </button>
                <button
                  v-if="['debt', 'hybrid conservative'].includes((schemeAssetAllocation.FundCategory || '').toLowerCase())"
                  class="custom-tab text-center"
                  :class="{ active: activeDebtTab === 'maturity' }"
                  @click="activeDebtTab = 'maturity'"
                >
                  Maturity & Yield
                </button>
              </div>
            </div>

            <!-- Credit Quality Section -->
            <div v-if="activeDebtTab === 'credit'">
  
              <div class="">
                <div
                  class="d-flex justify-content-between align-items-center me-3"
                  style="min-width: 120px"
                >
                  <span>Sovereign</span>
                  <span>{{ formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedSovereign ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2 w-100">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgba(0, 227, 150, 0.5)"
                      :style="{
                        width: formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedSovereign ?? 0),
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="mb-3 mt-2 align-items-center">
                <div
                  class="me-3 d-flex justify-content-between"
                  style="min-width: 120px"
                >
                  <span>&gt; AAA rating</span>
                  <span>{{ formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedAAA ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgb(0, 143, 251)"
                      :style="{
                        width: formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedAAA ?? 0),
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="mb-3 mt-2 align-items-center">
                <div
                  class="me-3 d-flex justify-content-between"
                  style="min-width: 120px"
                >
                  <span>AA+ rating</span>
                  <span>{{ formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedAAPlus ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: rgb(255, 69, 96)"
                      :style="{
                        width: formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedAAPlus ?? 0),
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="mb-3 mt-2 align-items-center">
                <div
                  class="me-3 d-flex justify-content-between"
                  style="min-width: 120px"
                >
                  <span>&lt; AA+ rating</span>
                  <span>{{ formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedLessThanAAPlus ?? 0) }}</span>
                </div>
                <div class="d-flex align-items-center w-100 mt-3">
                  <div class="progress-bar-container me-2">
                    <div
                      class="progress-bar-fill"
                      style="background-color: #6f42c1"
                      :style="{
                        width: formatPercent(schemeAssetAllocation.DebtAllocation?.CreditQuality?.PercentRatedLessThanAAPlus ?? 0),
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="mt-4"
              v-if="activeDebtTab === 'maturity' && schemeAssetAllocation.DebtAllocation?.maturityYtm && ['debt', 'hybrid conservative'].includes((schemeAssetAllocation.FundCategory || '').toLowerCase())"
            >
              <div class="maturity-box mb-3 d-flex justify-content-between align-items-center">
                <span class="maturity-label">Average Maturity of the portfolio</span>
                <span class="maturity-value">{{ schemeAssetAllocation.DebtAllocation.maturityYtm.AverageMaturity }} Years</span>
              </div>
              <div class="maturity-box d-flex justify-content-between align-items-center">
                <span class="maturity-label">Yield to maturity (YTM%)</span>
                <span class="maturity-value">{{ schemeAssetAllocation.DebtAllocation.maturityYtm.YTM }}%</span>
              </div>
            </div>
          </div>

          <div cols="12" v-if="activeTab === 'others'">
        <div class="alert alert-info mb-3 text-center" style="background: #f8f9fa; color: #333; border: 1px solid #e0e0e0; font-size: 15px;">
          {{ schemeAssetAllocation.OtherAllocation.OthersText }}
        </div>
        <!-- Add any other Others content here, full width -->
      </div>
      
        </div>
      </b-col>
   
      <b-col
        :cols="activeTab === 'others' ? 12 : undefined"
        :lg="activeTab === 'others' ? undefined : 5"
        class="mb-4"
        v-if="activeTab !== 'others'"
      >
      <template v-if="activeTab === 'equity' && hasEquityAllocation">
        <div class="card shadow-sm p-3 h-100 mb-3">
          <p class="fw-bold">Top Sectors (Equity)</p>
          <div class="sector-list">
            <div
              v-for="(sector, idx) in equitySectorsWithOther"
              :key="sector.sector"
              class="sector-row"
              :class="{ 'sector-row-grey': idx % 2 === 0 }"
            >
              <span class="sector-dot" :style="{ backgroundColor: equityColors[idx] }"></span>
              <span class="sector-name">{{ sector.sector }}</span>
              <span class="sector-value">{{ parseFloat(sector.hold_percentage).toFixed(2) }}%</span>
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="activeTab === 'debt' && hasDebtAllocation">
        <div class="card shadow-sm p-3 h-100 mb-3">
          <p class="fw-bold" v-if="(schemeDetails.category || '').toLowerCase() === 'hybrid'">Top Instrument holding</p>
<p class="fw-bold" v-else>Debt Instrument holding</p>
          <div class="sector-list">
            <div
              v-for="(sector, idx) in debtSectorsWithOther"
              :key="sector.sector"
              class="sector-row"
              :class="{ 'sector-row-grey': idx % 2 === 0 }"
            >
              <span class="sector-dot" :style="{ backgroundColor: debtColors[idx] }"></span>
              <span class="sector-name">{{ sector.sector }}</span>
              <span class="sector-value">{{ parseFloat(sector.hold_percentage).toFixed(2) }}%</span>
            </div>
          </div>
        
        </div>
      </template>
    </b-col>
  </b-row>

  <div class="mb-5" v-if="lastSixmonthmoveEQ.EQ.length > 0">
    <p class="text-muted fw-bold">Top Equity holdings</p>
    <div class="table-container">
      <table
        class="freeze-table table table-bordered align-middle text-center mb-0"
      >
        <thead class="bg-light">
          <tr>
            <th class="sticky-col">Stock name</th>
            <th>Sector</th>
            <th>PE</th>
            <th>% of net assets</th>
            <th>6M Change</th>
          </tr>
        </thead>
        <tbody style="border: unset !important">
          <tr v-for="company in lastSixmonthmoveEQ.EQ" :key="company.FINCODE">
            <td class="sticky-col text-left">
              {{ company.companyName }}
            </td>
            <td>{{ company.sector }}</td>
            <td>
              {{
                company.pe !== null && company.pe !== '' ? company.pe : '--'
              }}
            </td>
            <td>
              {{
                company.netAssetsPercentage
                  ? parseFloat(company.netAssetsPercentage).toFixed(2) + '%'
                  : '-'
              }}
            </td>
            <td><monthlyRecord :records="company.monthly_records" /></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="mt-3" v-if="lastSixmonthmoveDT.DT.length > 0">
    <p class="text-muted fw-bold">Top Debt holdings</p>

    <div class="table-container">
      <table
        class="freeze-table table table-bordered align-middle text-center mb-0"
      >
        <thead
          class="bg-light"
          v-if="lastSixmonthmoveDT && lastSixmonthmoveDT.DT"
        >
          <tr>
            <th class="sticky-col">Stock name</th>
            <th>Sector</th>
            <th>Credit rating</th>
            <th>% of net assets</th>
            <th>6M Change</th>
          </tr>
        </thead>
        <tbody style="border: unset !important">
          <tr v-for="company in lastSixmonthmoveDT.DT" :key="company.FINCODE">
            <td class="sticky-col text-left">
              {{ company.companyName }}
            </td>
            <td>{{ company.sector }}</td>
            <td>
              {{
                company.rating != null && company.rating !== ''
                  ? company.rating
                  : '--'
              }}
            </td>
            <td>
              {{
                company.netAssetsPercentage
                  ? parseFloat(company.netAssetsPercentage).toFixed(2) + '%'
                  : '-'
              }}
            </td>
            <td>
              <monthlyRecord :records="company.monthly_records" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
<div class="mt-3" v-if="lastSixmonthmoveFOF.fof.length > 0">
  <p class="text-muted fw-bold">Top FOF holdings</p>

  <div class="table-container">
    <table
      class="freeze-table table table-bordered align-middle text-center mb-0"
    >
      <thead
        class="bg-light"
        v-if="lastSixmonthmoveFOF && lastSixmonthmoveFOF.fof"
      >
        <tr>
          <th class="sticky-col">Stock name</th>
          <th>% of net assets</th>
          <th>6M Change</th>
        </tr>
      </thead>
      <tbody style="border: unset !important">
        <tr v-for="company in lastSixmonthmoveFOF.fof" :key="company.FINCODE">
          <td class="sticky-col text-left">
            {{ company.companyName }}
          </td>
          <td>
            {{
              company.netAssetsPercentage
                ? parseFloat(company.netAssetsPercentage).toFixed(2) + '%'
                : '-'
            }}
          </td>
          <td>
            <monthlyRecord :records="company.monthly_records" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import monthlyRecord from './monthlyRecord.vue'
import ApexCharts from 'vue3-apexcharts'

import { defineExpose } from 'vue'

const apexchart = ApexCharts
defineExpose({ apexchart })
const store = useStore()
const activeTab = ref('equity')

const EQSectors = computed(
  () => store.getters.schemeData?.schemeTopsectiors.EQ || []
)
const DTSectors = computed(
  () => store.getters.schemeData?.schemeTopsectiors.DT || []
)
const chartWidth = ref('350')
const activeDebtTab = ref('credit')
const lastChartTab = ref('equity')

function updateChartWidth() {
  chartWidth.value = window.innerWidth <= 768 ? '340' : '350'
}

function generateColors(count) {
  const baseColors = [
    '#008FFB',
    '#00E396',
    '#FEB019',
    '#FF4560',
    '#775DD0',
    '#3F51B5',
    '#03A9F4',
    '#4CAF50',
    '#F9CE1D',
    '#FF9800',
    '#D72638',
    '#1B998B',
    '#2E294E',
    '#E71D36',
    '#F4A261',
  ];
  const colors = [];
  for (let i = 0; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }
  return colors;
}



const lastSixmonthmoveEQ = computed(() => {
  const eq = schemeLastSixMonth.value.EQ || []
  return {
    EQ: eq.map((company) => ({
      companyName: company.companyName,
      sector: company.sector,
      FINCODE: company.FINCODE,
      pe: company.PE,
      netAssetsPercentage: company.HOLDPERCENTAGE,
      monthly_records: company.monthly_records || [],
    })),
  }
})
const lastSixmonthmoveFOF = computed(() => {
  const fof = schemeLastSixMonth.value.FOF || []
  return {
    fof: fof.map((company) => ({
      companyName: company.companyName,
      netAssetsPercentage: company.HOLDPERCENTAGE,
      monthly_records: company.monthly_records || [],
    })),
  }
})
const lastSixmonthmoveDT = computed(() => {
  const DT = schemeLastSixMonth.value.DT || []
  return {
    DT: DT.map((company) => ({
      companyName: company.companyName,
      sector: company.sector,
      FINCODE: company.FINCODE,
      rating: company.RATING,
      netAssetsPercentage: company.HOLDPERCENTAGE,
      monthly_records: company.monthly_records || [],
    })),
  }
})
const schemeAssetAllocation = computed(() => store.getters.schemeData?.schemeassetallocation || {});
const schemeLastSixMonth = computed(
  () => store.getters.schemeData?.lastSixmonthmove || {}
)
const schemeDetails = computed(() => store.getters.schemeData?.schemedetails || {});

function formatPercent(val) {
  return val ? `${parseFloat(val).toFixed(2)}%` : '0%'
}
const hasEquityAllocation = computed(() => {
  const alloc = schemeAssetAllocation.value.EquityAllocation;
  const val = schemeAssetAllocation.value.EquityAllocationPercentage;
  return alloc && val !== undefined && val !== null && typeof val === 'number' && val > 0;
});
const hasDebtAllocation = computed(() => {
  const alloc = schemeAssetAllocation.value.DebtAllocation;
  const val = schemeAssetAllocation.value.DebtAllocationPercentage;
  return alloc && val !== undefined && val !== null && typeof val === 'number' && val > 0;
});
const hasOtherAllocation = computed(() => {
  const val = schemeAssetAllocation.value.OtherAllocationPercentage;
  return val !== undefined && val !== null && typeof val === 'number' && val > 0;
});
const hasAnyAllocation = computed(() => hasEquityAllocation.value || hasDebtAllocation.value || hasOtherAllocation.value);

const isEquityMarketCapEmpty = computed(() => {
  const emc = schemeAssetAllocation.value.EquityAllocation?.EquityMarketCap;
  if (!emc) return true;
  // Check if all values are null, undefined, or zero
  const keys = ['LargeCapPercentage', 'MidCapPercentage', 'SmallCapPercentage', 'OverseasPercentage'];
  return keys.every(k => !emc[k] || parseFloat(emc[k]) === 0);
});
const derivativesExposure = computed(() => schemeAssetAllocation.value.EquityAllocation?.DerivativesExposure || null);

const derivativesExposureLastModifiedDate = computed(() => {
  const val = derivativesExposure.value?.LastModified;
  if (!val) return '-';
  // Expecting format 'YYYY-MM-DD HH:mm:ss', return only date part
  return val.split(' ')[0];
});

const debtSectorsWithOther = computed(() => {
  const sectors = DTSectors.value.map(item => ({
    sector: item.sector,
    hold_percentage: Number(item.hold_percentage) || 0
  }));
  return sectors;
});
const debtColors = computed(() => {
  const count = debtSectorsWithOther.value.length;
  const baseColors = generateColors(count);
  // If 'Other' is present, set its color to #205AA6
  if (debtSectorsWithOther.value[count - 1]?.sector === 'Other') {
    baseColors[count - 1] = '#205AA6';
  }
  return baseColors;
});

const equitySectorsWithOther = computed(() => {
  const sectors = EQSectors.value.map(item => ({
    sector: item.sector,
    hold_percentage: Number(item.hold_percentage) || 0
  }));
  return sectors;
});
const equityColors = computed(() => {
  const count = equitySectorsWithOther.value.length;
  const baseColors = generateColors(count);
  if (equitySectorsWithOther.value[count - 1]?.sector === 'Other') {
    baseColors[count - 1] = '#205AA6';
  }
  return baseColors;
});

// Set the initial active tab based on available allocations
onMounted(() => {
  if (!hasEquityAllocation.value) {
    if (hasDebtAllocation.value) activeTab.value = 'debt';
    else if (hasOtherAllocation.value) activeTab.value = 'others';
  }
  updateChartWidth()
  window.addEventListener('resize', updateChartWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateChartWidth)
})

watch(
  [() => hasEquityAllocation.value, () => hasDebtAllocation.value, () => hasOtherAllocation.value],
  ([eq, debt, other]) => {
    // If the current tab is not available, switch to the first available tab
    if (activeTab.value === 'equity' && !eq) {
      if (debt) activeTab.value = 'debt';
      else if (other) activeTab.value = 'others';
    } else if (activeTab.value === 'debt' && !debt) {
      if (eq) activeTab.value = 'equity';
      else if (other) activeTab.value = 'others';
    } else if (activeTab.value === 'others' && !other) {
      if (eq) activeTab.value = 'equity';
      else if (debt) activeTab.value = 'debt';
    }
  },
  { immediate: false }
);

watch(
  () => activeTab.value,
  (newTab) => {
    if (newTab === 'equity' || newTab === 'debt') {
      lastChartTab.value = newTab;
    }
  }
);
</script>

<style scoped>
.apexcharts-legend-series {
  margin: 10px 5px !important;
}

.equitydebt-graph {
  justify-items: center;
}

.table-container {
  overflow-x: hidden;
  background: #ffffff;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
}

.tab-row-center {
  display: flex;
  justify-content: center;
  width: 100%;
}
.tab-row {
  display: inline-flex;
  background-color: #f1f1f1;
  overflow: hidden;
  width: auto;
  margin: 0;
  border-radius: 10px;;
}
.custom-tab {
  background: transparent;
  border: none;
  padding: 8px 12px;
  font-weight: 600;
  color: #6c757d;
  transition: background 0.3s, color 0.3s;
  outline: none;
  box-shadow: none;
}
.custom-tab.active {
  background: #007bff;
  color: #fff;
}

.custom-tab:focus {
  outline: none;
  box-shadow: none;
}

@media (max-width: 500px) {
  .table-container {
    border-radius: 0;
    margin-left: -16px;
    margin-right: -16px;
  }
}

.table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 2;
  box-shadow: 2px 0px 5px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  max-width: 200px;
  width: auto;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.freeze-table th {
  text-align: center;
  background-color: #f8f9fa;
}

.freeze-table td,
.freeze-table th {
  padding: 10px;
  border: 1px solid #ddd;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 150px;
  white-space: normal;
  word-break: break-word;
}

@media (max-width: 767px) {
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .freeze-table th,
  .freeze-table td {
    min-width: 200px;
  }
}

@media (max-width: 1200px) {
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .freeze-table {
    min-width: 800px;
    width: 100%;
  }
}

@media (max-width: 1150px) {
  .scheme-allocation-text {
    font-size: 12px !important;
  }
}

.scheme-allocation-text {
  border-radius: 10px;
}

.card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #e6e6e9;
}

.progress-bar-fill {
  height: 100%;
  min-width: 2px;
  border-radius: 4px;
}

.progress-value {
  font-size: 12px;
  font-weight: 500;
  color: #000;
}

.sector-list {
  padding: 12px 0;
}
.sector-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
}
.sector-row-grey {
  background: #f8f9fa;
}
.sector-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 12px;
}
.sector-name {
  flex: 1;
  margin-left: 8px;
  font-size: 15px;
  color: #222;
}
.sector-value {
  font-weight: 500;
  color: #222;
  font-size: 15px;
}
.maturity-box {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.maturity-label {
  font-weight: 600;
  color: #222;
  font-size: 16px;
}
.maturity-value {
  font-weight: bold;
  color: #0a2e3b;
  font-size: 18px;
}
</style>
